import React from 'react';
import {
    Box,
    Stack,
    Typography,
    Button,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormControl,
    FormLabel,
    Dialog,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import ReactMarkdown from 'react-markdown';

import { useStyle } from './styles';
import RenderIf from '../../../renderIf';
import useRenewals from './useRenewals';

const CreateNew = (props) => {
    const { applicationDefinitionId, close } = props;

    const { renewals, term, initialized, doingAjax, retrieveRenewals, startApplication } = useRenewals(applicationDefinitionId, close);

    const classes = useStyle();

    const [opt, setOpt] = React.useState('');
    const [renewalStudent, setRenewalStudent] = React.useState(-1);
    const [invalid, setInvalid] = React.useState({ select: false, radio: false });

    React.useEffect(() => {
        retrieveRenewals();
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (opt === '2' && (renewalStudent === -1 || renewalStudent === 'N/A')) {
            setInvalid({ select: opt === '', radio: renewalStudent === -1 || renewalStudent === 'N/A' });
            toast.error(t('General.ValidationError'));
        } else {
            startApplication(renewalStudent);
        }
    };

    const handleRadioChange = (event) => {
        setRenewalStudent(event.target.value);
        setInvalid((prev) => {
            return { ...prev, radio: false };
        });
    };

    return (
        <Dialog open={true}>
            <DialogTitle>
                <Markdown>{t('CreateNew.Header', { Term: term })}</Markdown>
            </DialogTitle>
            <DialogContent>
                <Box>
                    <RenderIf isTrue={!initialized}>
                        <Typography className={classes.modalContent}>{t('CreateNew.Initializing')}</Typography>
                    </RenderIf>

                    <RenderIf isTrue={initialized}>
                        <form>
                            <FormControl
                                sx={{
                                    '& > p': {
                                        fontSize: 'calc(15rem/16)',
                                        margin: '0 0 calc(3rem/16) 0',
                                        '&, & span.MuiBox-root': {
                                            color: '#586874',
                                        },
                                    },
                                }}
                            >
                                <Typography
                                    sx={{ mt: 'calc(10rem/16)' }}
                                    className={classes.modalContent}
                                >
                                    {t('CreateNew.Instructions.Intro')}
                                </Typography>

                                <Typography className={classes.modalContent}>
                                    <Markdown>{t('CreateNew.Instructions.Type1')}</Markdown>
                                </Typography>

                                <Typography
                                    sx={{
                                        '&.MuiTypography-root': {
                                            mb: 'calc(20rem/16)',
                                        },
                                    }}
                                    className={classes.modalContent}
                                >
                                    <Markdown>{t('CreateNew.Instructions.Type2')}</Markdown>
                                </Typography>

                                <Stack
                                    sx={{
                                        mb: 'calc(21rem/16)',
                                    }}
                                >
                                    <FormLabel
                                        id="type-select-label"
                                        error={invalid.select}
                                        sx={{
                                            color: '#586874',
                                            '& p': {
                                                fontSize: 'calc(14rem/16)',
                                                fontWeight: 600,
                                                margin: '0 0 calc(5rem/16) 0',
                                            },
                                        }}
                                    >
                                        <Typography className={classes.modalContent}>{t('CreateNew.Responses.TypeLabel')}</Typography>
                                    </FormLabel>

                                    <Typography
                                        component="select"
                                        id="type-select"
                                        aria-labelledby="type-select-label"
                                        value={opt}
                                        onChange={(event) => {
                                            setOpt(event.target.value);
                                            setRenewalStudent(-1);
                                            setInvalid((prev) => {
                                                return { ...prev, select: false, radio: false };
                                            });
                                        }}
                                        className={false ? [classes.invalid, classes.select].join(' ') : classes.select}
                                        sx={{
                                            '&.MuiTypography-root': {
                                                width: 'calc(300rem/16)',
                                                fontSize: 'calc(14rem/16)',
                                                padding: 'calc(8rem/16) calc(9rem/16)',
                                                border: '1px solid #CACDD1',
                                                borderRadius: '4px',
                                            },
                                        }}
                                    >
                                        <Typography
                                            component="option"
                                            value=""
                                        />
                                        <Typography
                                            component="option"
                                            value="1"
                                        >
                                            Initial
                                        </Typography>
                                        <Typography
                                            component="option"
                                            value="2"
                                        >
                                            Renewal
                                        </Typography>
                                    </Typography>
                                </Stack>

                                <RenderIf isTrue={opt === '2'}>
                                    <FormLabel
                                        id="renewal-list-radios"
                                        sx={{
                                            '& > p': {
                                                fontSize: 'calc(14rem/16)',
                                                margin: 0,
                                            },
                                        }}
                                        error={invalid.radio}
                                    >
                                        <Typography className={classes.modalContent}>
                                            <IsRequired /> <Markdown>{t('CreateNew.Responses.StudentListLabel')}</Markdown>
                                        </Typography>
                                    </FormLabel>
                                    <RadioGroup
                                        aria-labelledby="renewal-list-radios"
                                        onChange={handleRadioChange}
                                        sx={{
                                            '& .MuiButtonBase-root': {
                                                color: '#494FF9',
                                            },
                                            '& span.MuiFormControlLabel-label': {
                                                fontSize: 'calc(15rem/16)',
                                            },
                                            '&:has(.Mui-checked)': {
                                                '& span.MuiFormControlLabel-label': {
                                                    color: '#586874',
                                                    fontWeight: 700,
                                                },
                                            },
                                        }}
                                    >
                                        {renewals.map((item) => {
                                            return (
                                                <FormControlLabel
                                                    value={item.UniqueID}
                                                    control={<Radio />}
                                                    label={item.StudentName ? item.StudentName : 'No Name'}
                                                />
                                            );
                                        })}
                                        <FormControlLabel
                                            value="N/A"
                                            control={<Radio />}
                                            label="My student isn't listed here"
                                        />
                                    </RadioGroup>
                                </RenderIf>

                                <RenderIf isTrue={opt !== '' && renewalStudent !== 'N/A'}>
                                    <Typography
                                        className={classes.modalContent}
                                        sx={{
                                            color: 'approved.main',
                                            fontWeight: 700,
                                            marginTop: '1rem',
                                        }}
                                    >
                                        {t('CreateNew.Informational.ForTerm', { Term: term })}
                                    </Typography>
                                </RenderIf>

                                <RenderIf isTrue={opt === '2' && renewalStudent === 'N/A'}>
                                    <Typography
                                        className={classes.modalContent}
                                        sx={{
                                            '&.MuiTypography-root': {
                                                color: '#014E8D',
                                                fontWeight: 600,
                                                fontSize: 'calc(15rem/16)',
                                                margin: 'calc(24rem/16) 0 calc(14rem/16) 0',
                                            },
                                        }}
                                    >
                                        {t('CreateNew.Informational.NotListed')}
                                    </Typography>

                                    <Typography
                                        className={classes.modalContent}
                                        sx={{
                                            '&.MuiTypography-root': {
                                                '&, & span.MuiBox-root': {
                                                    color: '#231C32',
                                                },
                                                fontSize: 'calc(15rem/16)',
                                            },
                                        }}
                                    >
                                        <Markdown>{t('CreateNew.Informational.ContactInfo.Phone')}</Markdown>
                                        <br />
                                        <Markdown>{t('CreateNew.Informational.ContactInfo.Email')}</Markdown>
                                    </Typography>
                                </RenderIf>
                            </FormControl>
                        </form>
                    </RenderIf>
                </Box>
                <Box>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => close?.()}
                    >
                        {t('General.Cancel')}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={doingAjax || opt === '' || renewalStudent === 'N/A'}
                    >
                        {t('CreateNew.StartApplication')}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

const IsRequired = () => {
    return (
        <Typography
            component="span"
            sx={{ color: (theme) => theme.palette.error.main }}
        >
            *
        </Typography>
    );
};

const Markdown = (props) => {
    const { children } = props;

    return (
        <ReactMarkdown
            disallowedElements={['p']}
            unwrapDisallowed
            components={{
                strong: ({ node, ...props }) => (
                    <Box
                        sx={{ fontWeight: 600 }}
                        {...props}
                        component="span"
                    />
                ),
                code: ({ node, inline, ...props }) => (
                    <Box
                        sx={{ color: (theme) => theme.palette.approved.main, fontWeight: 550 }}
                        {...props}
                        component="span"
                    />
                ),
            }}
        >
            {children}
        </ReactMarkdown>
    );
};

export default CreateNew;
